import type { ResellerStatusFilter } from "@kilat/contracts/reseller";
import { queryOptions } from "@tanstack/react-query";
import type { EntryFilters } from "@/features/billing/api";
import {
  getCurrentStaff,
  getReseller,
  getResellerWallet,
  listResellers,
  listResellerWalletEntries,
  listStaff,
  listTldPricing,
  listStaffTopUps,
  type StaffTopUpFilter,
} from "./api";

// One reseller sits under resellers() with the lists, so a decision that invalidates resellers()
// refreshes the queue, the list and the reseller's own page at once.
export const staffKeys = {
  all: ["staff"] as const,
  me: () => [...staffKeys.all, "me"] as const,
  list: () => [...staffKeys.all, "list"] as const,
  resellers: () => [...staffKeys.all, "resellers"] as const,
  reseller: (resellerId: string) => [...staffKeys.resellers(), "detail", resellerId] as const,
  wallet: (resellerId: string) => [...staffKeys.reseller(resellerId), "wallet"] as const,
  tldPricing: () => [...staffKeys.all, "tld-pricing"] as const,
  topUps: () => [...staffKeys.all, "top-ups"] as const,
};

/** Who the staff area is signed in as: the staff boundary resolves it once. */
export const currentStaffQuery = () =>
  queryOptions({ queryKey: staffKeys.me(), queryFn: async () => (await getCurrentStaff()).data });

export const staffListQuery = () =>
  queryOptions({ queryKey: staffKeys.list(), queryFn: async () => (await listStaff()).data });

/** The applications waiting for a decision, or the resellers with another status ("all": every one). */
export const resellersQuery = (status: ResellerStatusFilter = "pending") =>
  queryOptions({
    queryKey: [...staffKeys.resellers(), status] as const,
    queryFn: async () => (await listResellers(status)).data,
  });

/** One reseller, whatever its status, with what it sent and the decision on it. */
export const resellerQuery = (resellerId: string) =>
  queryOptions({ queryKey: staffKeys.reseller(resellerId), queryFn: async () => (await getReseller(resellerId)).data });

/** Every TLD on sale, in display order, with what each term costs KilatDomain. */
export const tldPricingQuery = () =>
  queryOptions({ queryKey: staffKeys.tldPricing(), queryFn: async () => (await listTldPricing()).data });

/** One reseller's credit balance, as support reads it. */
export const resellerWalletQuery = (resellerId: string) =>
  queryOptions({
    queryKey: [...staffKeys.wallet(resellerId), "balance"] as const,
    queryFn: async () => (await getResellerWallet(resellerId)).data,
  });

/** One page of a reseller's billing history, newest first, narrowed as the reseller narrows its own. */
export const resellerWalletEntriesQuery = (resellerId: string, page: number, filters: EntryFilters) =>
  queryOptions({
    queryKey: [...staffKeys.wallet(resellerId), "entries", page, filters] as const,
    queryFn: () => listResellerWalletEntries(resellerId, page, filters),
  });

/** One page of the top-up queue: disputes first, then the newest, by state and by reseller. */
export const staffTopUpsQuery = (filter: StaffTopUpFilter) =>
  queryOptions({ queryKey: [...staffKeys.topUps(), filter] as const, queryFn: () => listStaffTopUps(filter) });
